import { useState, useEffect, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import _ from 'lodash'
import { UncontrolledPopover } from 'reactstrap'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import moment from 'moment'

import { setCurrentTab, showSideBar, showNavBar } from '../../actions/commonActions'
import { getCoaches, inviteCoach, reinviteCoach } from '../../actions/coachActions'
import { PER_PAGE_DEFAULT, START_PAGE_DEFAULT } from '../../misc/Constants'

import Loader from '../common/loader/legacy-loaders/Op36Loader-web'
import InviteCoachModal from './InviteCoachModal'
import Placeholder from '../common/Placeholder'
import SearchInput from '../common/SearchInput'
import Pagination from '../common/Pagination'
import PageHeader from '../common/PageHeader/PageHeader'

import AvatarPlaceholder from '../../assets/images/mock/default-profile-icon.png'
import classes from './CoachesPage.module.scss'

const filterOptions = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'invited', label: 'Invited' },
]

const CoachesPage = () => {
  const dispatch = useDispatch()
  const coaches = useSelector(state => state.coaches)

  const [showInviteCoachModal, setShowInviteCoachModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [coachLoader, setCoachLoader] = useState(true)
  const [currentPage, setCurrentPage] = useState(START_PAGE_DEFAULT)
  const [statusFilter, setStatusFilter] = useState('all')

  const fetchCoachesData = useCallback((page = START_PAGE_DEFAULT) => {
    setCoachLoader(true)
    setCurrentPage(page)
    const status = statusFilter === 'all' ? null : statusFilter
    dispatch(getCoaches(searchTerm, page, PER_PAGE_DEFAULT, status)).finally(() => { setCoachLoader(false) })
  }, [dispatch, searchTerm, statusFilter])

  useEffect(() => {
    dispatch(setCurrentTab('Coaches'))
    dispatch(showSideBar(true))
    dispatch(showNavBar(true))
    fetchCoachesData(START_PAGE_DEFAULT)
  }, [dispatch, fetchCoachesData])

  const debouncedFetchCoaches = useMemo(
    () => _.debounce(() => fetchCoachesData(START_PAGE_DEFAULT), 500),
    [fetchCoachesData]
  )

  const handleSearchChange = (e) => {
    const term = e.target.value
    setSearchTerm(term)
    if (term.length === 0 || term.length >= 3) {
      debouncedFetchCoaches()
    }
  }

  const handleInviteCoach = (inviteData) => (
    dispatch(inviteCoach(inviteData))
      .then(() => {
        setShowInviteCoachModal(false)
        fetchCoachesData(currentPage)
        return true
      })
      .catch(() => false)
  )

  const handleReinviteCoach = (coach) => {
    reinviteCoach(coach.id)
      .then(() => {
        toast.success('Coach reinvitation sent successfully', { position: toast.POSITION.TOP_RIGHT })
      })
      .catch(() => {
        toast.error('Something went wrong', { position: toast.POSITION.TOP_RIGHT })
      })
  }

  const handleFilterChange = (filter) => {
    setStatusFilter(filter)
  }

  const coachList = coaches.coaches || []
  const coachCount = coaches.pagination?.total_count || coachList.length

  const inviteCoachButton = (
    <button className='btn btn-primary text-white pull-right mr-2' onClick={() => setShowInviteCoachModal(true)}>
      Invite Coach
    </button>
  )

  return (
    <div className={classes.coachesPage}>
      <PageHeader title='Coaches' actionComponent={inviteCoachButton} />

      <div className={classNames(classes.coachesContent, 'w-100')}>
        <div className={classNames(classes.coachesHeader, 'w-100 d-flex justify-content-between align-items-center')}>
          <h1 className={classNames(classes.coachesTitle, 'mr-3')}>Coaches ({coachCount})</h1>

          <div className={classNames(classes.searchBox, 'mx-3')}>
            <SearchInput placeholder='Search for Coaches' onChange={handleSearchChange}/>
          </div>

          <div className='d-flex align-items-center ml-auto'>
            {filterOptions.map(filter => (
              <button
                key={filter.value}
                className={classNames(classes.filterButton, { [classes.filterButtonActive]: statusFilter === filter.value }, 'ml-2')}
                onClick={() => handleFilterChange(filter.value)}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {coachLoader ? (
          <Loader message={'Loading Coaches'} />
        ) : coachList.length === 0 ? (
          <div className='col-6 mx-auto'>
            <Placeholder
              title={'No Coaches Found'}
              icon={'fa-user'}
              subtext1={'Use the search bar to find coaches or add a new coach'}
            />
          </div>
        ) : (
          <div className={classes.coachesTableContainer}>
            <table className='table table-borderless w-100 mb-0'>
              <thead>
                <tr className={classes.coachesTableHeader}>
                  <th>Coach</th>
                  <th>Email</th>
                  <th>Community</th>
                  <th>Phone Number</th>
                  <th>Date Created</th>
                  <th>Observations</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {coachList.map((coach, index) => {
                  const isLastRow = index === coachList.length - 1 && coaches.pages <= 1
                  return (
                    <tr key={coach.id} className={classNames(classes.coachRow, { [classes.coachRowLast]: isLastRow })}>
                      <td>
                        <div className='d-flex align-items-center'>
                          <img
                            src={coach.avatar?.file_url || AvatarPlaceholder}
                            alt={`${coach.first_name} ${coach.last_name}`}
                            className={classes.coachAvatar}
                          />
                          <span className='font-weight-bold ml-2'>{coach.first_name} {coach.last_name}</span>
                        </div>
                      </td>
                      <td>{coach.email}</td>
                      <td>
                        <Link to={`/communities/${coach.communities[0].id}`} target='_blank' rel='noopener noreferrer'>
                          {coach.communities[0].name}
                        </Link>
                      </td>
                      <td>{coach.phone_number || 'N/A'}</td>
                      <td>{coach.created_at ? moment(coach.created_at).format('MMM D, YYYY') : 'N/A'}</td>
                      <td>
                        <span className={classNames(classes.coachStatus, { [classes.statusInvited]: coach.status === 'invited' })}>
                          {coach.status === 'invited' ? 'Pending Invitation' : 'Active Member'}
                        </span>
                      </td>
                      <td className={`${classes.coachCell} ${classes.coachActionsCell}`}>
                        <button
                          id={`coach-actions-${coach.id}`}
                          className={classNames(classes.coachActionsIcon, 'd-inline-flex', 'align-items-center', 'justify-content-center')}
                          aria-label='More actions'
                        >
                          <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
                            <circle cx='12' cy='5' r='2' fill='#36434E'/>
                            <circle cx='12' cy='12' r='2' fill='#36434E'/>
                            <circle cx='12' cy='19' r='2' fill='#36434E'/>
                          </svg>
                        </button>
                        <UncontrolledPopover
                          placement='bottom'
                          target={`coach-actions-${coach.id}`}
                          trigger='legacy'
                          popperClassName='popover-style'
                        >
                          <div className={classNames('d-flex', 'flex-column', classes.coachActions)}>
                            <Link to={`/profile/${coach.id}`}>View Profile</Link>
                            {coach.status === 'invited' ? (
                              <button onClick={() => handleReinviteCoach(coach)}>
                                Reinvite
                              </button>
                            ) : (
                              <button onClick={() => {}}>
                                Edit
                              </button>
                            )}
                          </div>
                        </UncontrolledPopover>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {coaches.pages > 1 && (
              <div className='mt-4'><Pagination pageCount={coaches.pages} fetchData={fetchCoachesData} activePage={currentPage} /></div>
            )}
          </div>
        )}
      </div>

      <InviteCoachModal
        isOpen={showInviteCoachModal}
        onClose={() => setShowInviteCoachModal(false)}
        onSubmit={handleInviteCoach}
      />
    </div>
  )
}

export default CoachesPage
